import { useTranslation } from '@dreamstack/i18n'
import { SimpleInput } from '@dreamstack/simple-components'
import { generateIdForText } from '@dreamstack/util'
import Image from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import tw from 'twin.macro'
import { FormCheckbox } from '../forms/FormCheckbox'
import {
  IS_ACCENTRO_INVESTORS,
  IS_ACCENTRO_WEB,
  useIsMdOrGreater,
  useNotificationContext,
} from '../index'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { HeadingH2 } from '../layout/Texts'
import type { contactFormType } from './typings'
import { useContactFormSchema } from './useContactFormSchema'
import { useSubmitContactForm } from './useSubmitContactForm'

const IS_INTERNATIONAL = !IS_ACCENTRO_WEB && !IS_ACCENTRO_INVESTORS

const FormFields = tw.div`grid grid-cols-1 gap-px-24 md:(grid-cols-2)`

export type ContactFormProps = {
  type?: contactFormType
}

export const ContactForm: FunctionComponent<
  React.PropsWithChildren<ContactFormProps>
> = ({ type }) => {
  const t = useTranslation()
  const isMd = useIsMdOrGreater()

  const yupSchemaResolver = useContactFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const { handleSubmit, control, reset } = methods

  const onSuccess = () => {
    addNotification('success', t('accentro:contactform.successMessage'))
    reset()
  }
  const { submitFn: onSubmit, loading } = useSubmitContactForm({
    successCallback: onSuccess,
    formId: 'contact',
    type: type ?? 'contactform',
  })

  const { addNotification } = useNotificationContext()
  const title = t('accentro:contactform.contactform')
  return (
    <div>
      <HeadingH2 id={generateIdForText(title)} tw="mb-px-48 md:(mb-px-64)">
        {title}
      </HeadingH2>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormFields>
            {!IS_INTERNATIONAL && (
              <Controller
                defaultValue=""
                name="clientSalutation"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={t('accentro:contactform.salutation')}
                    fullWidth
                  />
                )}
              />
            )}
            <div tw="col-start-1">
              <Controller
                defaultValue=""
                name="clientFirstName"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={t('accentro:contactform.firstname')}
                    fullWidth
                  />
                )}
              />
            </div>
            <Controller
              defaultValue=""
              name="clientLastName"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={t('accentro:contactform.lastname')}
                  fullWidth
                />
              )}
            />
            {!IS_INTERNATIONAL && (
              <>
                <Controller
                  defaultValue=""
                  name="clientStreet"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.street')}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  defaultValue=""
                  name="clientStreetnumber"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.streetnumber')}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  defaultValue=""
                  name="clientZipCode"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.zipcode')}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  defaultValue=""
                  name="clientCity"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.city')}
                      fullWidth
                    />
                  )}
                />
              </>
            )}
            <Controller
              defaultValue=""
              name="clientEmail"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={`${t('accentro:contactform.email')}*`}
                  fullWidth
                />
              )}
            />
            <Controller
              defaultValue=""
              name="clientPhone"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={`${t('accentro:contactform.phone')}*`}
                  fullWidth
                />
              )}
            />
            {!IS_INTERNATIONAL && (
              <div tw="col-span-full">
                <Controller
                  defaultValue=""
                  name="subject"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.subject')}
                      fullWidth
                    />
                  )}
                />
              </div>
            )}
            {IS_INTERNATIONAL && (
              <div tw="col-span-full">
                <Controller
                  defaultValue=""
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.country')}
                      fullWidth
                    />
                  )}
                />
              </div>
            )}
            <div tw="col-span-full">
              <Controller
                defaultValue=""
                name="text"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.message')}*`}
                    multiline
                    minRows={4}
                    fullWidth
                  />
                )}
              />
            </div>

            {IS_INTERNATIONAL && (
              <div tw="col-span-full bg-accentroAquaVeryLight-full flex flex-row text-px-20 px-px-64 py-px-48">
                <div tw="flex-1 pr-px-64">
                  <div tw="text-px-28 font-semibold mb-px-24">
                    There is more for you!
                  </div>

                  <div>
                    With the 14. ACCENTRO Homeownership Report 2021 you get the
                    best real estate insights at a glance.
                  </div>

                  <div tw="mt-px-24">
                    <FormCheckbox
                      propertyName="subscribeHomeOwnerShipReport"
                      labelComponent={`${t(
                        'accentro:contactform.homeOwnershipReportConsentText'
                      )}`}
                    />
                  </div>
                </div>
                {isMd && (
                  <div tw="flex-1 relative">
                    <Image
                      src={'/static/images/WER-en.png'}
                      width={439}
                      height={346}
                      alt={'WER Report image'}
                    />
                  </div>
                )}
              </div>
            )}

            <div tw="col-span-full">
              <FormCheckbox
                propertyName="consent"
                labelComponent={`${t('accentro:contactform.consentText')}*`}
              />
            </div>
            {IS_ACCENTRO_WEB && (
              <div tw="col-span-full">
                <FormCheckbox
                  tw="flex self-start"
                  propertyName="subscribeNewsletter"
                  labelComponent={`${t(
                    'accentro:contactform.consentNewsletterText'
                  )}`}
                />
              </div>
            )}
            <div tw="col-span-full text-accentroGray-500">
              <p>{t('accentro:contactform.requiredFieldsText')}</p>
            </div>
            <SimpleMuiButton
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              tw="w-60"
              loading={loading}
            >
              {t('accentro:contactform.sendMessage')}
            </SimpleMuiButton>
          </FormFields>
        </form>
      </FormProvider>
    </div>
  )
}
